/**
 * Per-file upload progress indicator.
 * States: Uploading (blue %) → Processing ("Extracting metadata..." spinner)
 *         → Complete (green check) → Error (red X + Retry)
 */

import type { UploadPhase } from '@/lib/upload';
import { formatFileSize } from '@/lib/upload';

export interface UploadProgressBarProps {
  fileName: string;
  fileSize: number;
  phase: UploadPhase;
  progress: number; // 0-100
  error?: string;
  onRetry?: () => void;
}

export function UploadProgressBar({
  fileName,
  fileSize,
  phase,
  progress,
  error,
  onRetry,
}: UploadProgressBarProps) {
  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <FileIcon phase={phase} />
          <span className="truncate text-sm font-medium text-navy-900">
            {fileName}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatFileSize(fileSize)}
          </span>
        </div>
        <PhaseLabel phase={phase} progress={progress} />
      </div>

      {/* Progress bar */}
      {(phase === 'uploading' || phase === 'presigning') && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-brand-blue transition-all duration-300"
            style={{ width: `${phase === 'presigning' ? 0 : progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      )}

      {/* Processing spinner bar */}
      {phase === 'processing' && (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
          <div className="h-full w-1/3 animate-pulse rounded-full bg-brand-blue" />
        </div>
      )}

      {/* Error message + retry */}
      {phase === 'error' && error && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-destructive">{error}</span>
          {onRetry && (
            <button
              onClick={onRetry}
              className="text-xs font-medium text-brand-ink hover:underline"
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FileIcon({ phase }: { phase: UploadPhase }) {
  if (phase === 'complete') {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-600">
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </span>
    );
  }

  if (phase === 'error') {
    return (
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </span>
    );
  }

  return (
    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-blue-100 text-brand-ink">
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    </span>
  );
}

function PhaseLabel({ phase, progress }: { phase: UploadPhase; progress: number }) {
  switch (phase) {
    case 'presigning':
      return <span className="text-xs text-muted-foreground">Preparing...</span>;
    case 'uploading':
      return <span className="text-xs font-medium text-brand-ink">{progress}%</span>;
    case 'processing':
      return <span className="text-xs text-muted-foreground">Extracting metadata...</span>;
    case 'complete':
      return <span className="text-xs font-medium text-green-600">Complete</span>;
    case 'error':
      return <span className="text-xs font-medium text-destructive">Failed</span>;
    default:
      return null;
  }
}
